import { InstantPageAnalysisResult, PageChecks, PageTiming, PageMeta } from '../lib/instantPageService';

export interface CategoryScore {
  score: number;
  grade: 'A+' | 'A' | 'B' | 'C' | 'D' | 'F';
  status: 'excellent' | 'good' | 'average' | 'poor' | 'critical';
  issues: Issue[];
  recommendations: Recommendation[];
}

export interface Issue {
  id: string;
  title: string;
  description: string;
  severity: 'critical' | 'warning' | 'notice';
  impact: 'high' | 'medium' | 'low';
  category: string;
}

export interface Recommendation {
  id: string;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  effort: 'low' | 'medium' | 'high';
  impact: 'high' | 'medium' | 'low';
  category: string;
  howToFix: {
    steps: string[];
    codeExample?: string;
    resources: string[];
  };
  businessImpact: string;
  timeEstimate: string;
}

export interface AdvancedScoring {
  global: {
    score: number;
    grade: 'A+' | 'A' | 'B' | 'C' | 'D' | 'F';
    status: 'excellent' | 'good' | 'average' | 'poor' | 'critical';
    trend: 'improving' | 'stable' | 'declining';
  };
  categories: {
    performance: CategoryScore;
    seo: CategoryScore;
    ux: CategoryScore;
    technical: CategoryScore;
  };
  totalIssues: {
    critical: number;
    warnings: number;
    notices: number;
  };
  priorities: Recommendation[];
}

// Fonction pour convertir un score en grade
export const getGrade = (score: number): 'A+' | 'A' | 'B' | 'C' | 'D' | 'F' => {
  if (score >= 95) return 'A+';
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
};

// Fonction pour obtenir le statut basé sur le score
export const getStatus = (score: number): 'excellent' | 'good' | 'average' | 'poor' | 'critical' => {
  if (score >= 90) return 'excellent';
  if (score >= 80) return 'good';
  if (score >= 70) return 'average';
  if (score >= 60) return 'poor';
  return 'critical';
};

// Fonction pour créer un score par défaut quand les données sont manquantes
const getDefaultCategoryScore = (category: string): CategoryScore => {
  return {
    score: 0,
    grade: 'F',
    status: 'critical',
    issues: [{
      id: `${category}-no-data`,
      title: 'Données manquantes',
      description: `Aucune donnée disponible pour l'analyse ${category}`,
      severity: 'critical',
      impact: 'high',
      category
    }],
    recommendations: []
  };
};

// Calcul du score de performance avancé
export const calculatePerformanceScore = (timing: PageTiming | null | undefined, checks: PageChecks | null | undefined): CategoryScore => {
  // Vérification de sécurité pour les données manquantes
  if (!timing || !checks) {
    return getDefaultCategoryScore('performance');
  }

  let score = 100;
  const issues: Issue[] = [];
  const recommendations: Recommendation[] = [];

  // Core Web Vitals - avec vérifications de sécurité
  const lcp = timing.largest_contentful_paint || 0;
  const fid = timing.first_input_delay || 0;
  const tti = timing.time_to_interactive || 0;

  if (lcp > 4000) {
    score -= 25;
    issues.push({
      id: 'lcp-poor',
      title: 'Largest Contentful Paint trop lent',
      description: `LCP de ${lcp}ms dépasse les 4 secondes`,
      severity: 'critical',
      impact: 'high',
      category: 'performance'
    });
    recommendations.push({
      id: 'optimize-lcp',
      title: 'Optimiser le Largest Contentful Paint',
      description: 'Réduire le temps de chargement du plus grand élément visible',
      priority: 'high',
      effort: 'medium',
      impact: 'high',
      category: 'performance',
      howToFix: {
        steps: [
          'Optimiser les images principales',
          'Utiliser un CDN pour les ressources statiques',
          'Implémenter le lazy loading',
          'Minimiser le CSS critique'
        ],
        codeExample: '<img loading="lazy" src="image.jpg" alt="Description">',
        resources: [
          'https://web.dev/lcp/',
          'https://developers.google.com/speed/pagespeed/insights/'
        ]
      },
      businessImpact: 'Amélioration du taux de conversion de 7-12%',
      timeEstimate: '2-4 heures'
    });
  } else if (lcp > 2500) {
    score -= 10;
    issues.push({
      id: 'lcp-average',
      title: 'Largest Contentful Paint à améliorer',
      description: `LCP de ${lcp}ms peut être optimisé`,
      severity: 'warning',
      impact: 'medium',
      category: 'performance'
    });
  }

  if (fid > 300) {
    score -= 20;
    issues.push({
      id: 'fid-poor',
      title: 'First Input Delay trop élevé',
      description: `FID de ${fid}ms impacte l\'interactivité`,
      severity: 'critical',
      impact: 'high',
      category: 'performance'
    });
  }

  // Temps de chargement général
  if (tti > 5000) {
    score -= 20;
    issues.push({
      id: 'tti-poor',
      title: 'Temps d\'interactivité trop long',
      description: `${tti}ms pour devenir interactif`,
      severity: 'critical',
      impact: 'high',
      category: 'performance'
    });
  }

  // Vérifications techniques
  if (checks.high_loading_time) {
    score -= 15;
    issues.push({
      id: 'high-loading-time',
      title: 'Temps de chargement élevé détecté',
      description: 'Le serveur met trop de temps à répondre',
      severity: 'warning',
      impact: 'medium',
      category: 'performance'
    });
  }

  if (checks.has_render_blocking_resources) {
    score -= 10;
    issues.push({
      id: 'render-blocking',
      title: 'Ressources bloquant le rendu',
      description: 'CSS/JS bloquent l\'affichage initial',
      severity: 'warning',
      impact: 'medium',
      category: 'performance'
    });
  }

  return {
    score: Math.max(0, score),
    grade: getGrade(Math.max(0, score)),
    status: getStatus(Math.max(0, score)),
    issues,
    recommendations
  };
};

// Calcul du score SEO avancé
export const calculateSeoScore = (meta: PageMeta | null | undefined, checks: PageChecks | null | undefined): CategoryScore => {
  // Vérification de sécurité pour les données manquantes
  if (!meta || !checks) {
    return getDefaultCategoryScore('seo');
  }

  let score = 100;
  const issues: Issue[] = [];
  const recommendations: Recommendation[] = [];

  // Titre
  if (checks.no_title) {
    score -= 25;
    issues.push({
      id: 'no-title',
      title: 'Titre manquant',
      description: 'La page n\'a pas de balise title',
      severity: 'critical',
      impact: 'high',
      category: 'seo'
    });
  } else if (checks.title_too_long) {
    score -= 10;
    issues.push({
      id: 'title-too-long',
      title: 'Titre trop long',
      description: `Titre de ${meta.title_length || 0} caractères dépasse 60`,
      severity: 'warning',
      impact: 'medium',
      category: 'seo'
    });
  } else if (checks.title_too_short) {
    score -= 8;
    issues.push({
      id: 'title-too-short',
      title: 'Titre trop court',
      description: `Titre de ${meta.title_length || 0} caractères insuffisant`,
      severity: 'warning',
      impact: 'medium',
      category: 'seo'
    });
  }

  // Description
  if (checks.no_description) {
    score -= 20;
    issues.push({
      id: 'no-description',
      title: 'Meta description manquante',
      description: 'Aucune meta description définie',
      severity: 'critical',
      impact: 'high',
      category: 'seo'
    });
  }

  // Structure H1
  if (checks.no_h1_tag) {
    score -= 15;
    issues.push({
      id: 'no-h1',
      title: 'Balise H1 manquante',
      description: 'Aucune balise H1 trouvée sur la page',
      severity: 'critical',
      impact: 'high',
      category: 'seo'
    });
  }

  // Canonical
  if (!checks.canonical) {
    score -= 10;
    issues.push({
      id: 'no-canonical',
      title: 'URL canonique manquante',
      description: 'Aucune balise canonical définie',
      severity: 'warning',
      impact: 'medium',
      category: 'seo'
    });
  }

  // Images sans alt
  if (checks.no_image_alt) {
    score -= 8;
    issues.push({
      id: 'no-image-alt',
      title: 'Images sans attribut alt',
      description: 'Certaines images n\'ont pas d\'attribut alt',
      severity: 'warning',
      impact: 'medium',
      category: 'seo'
    });
  }

  return {
    score: Math.max(0, score),
    grade: getGrade(Math.max(0, score)),
    status: getStatus(Math.max(0, score)),
    issues,
    recommendations
  };
};

// Calcul du score UX
export const calculateUxScore = (meta: PageMeta | null | undefined, checks: PageChecks | null | undefined): CategoryScore => {
  // Vérification de sécurité pour les données manquantes
  if (!meta || !checks) {
    return getDefaultCategoryScore('ux');
  }

  let score = 100;
  const issues: Issue[] = [];
  const recommendations: Recommendation[] = [];

  // Lisibilité
  const readabilityScore = meta.content?.flesch_kincaid_readability_index || 0;
  if (readabilityScore > 60) {
    score -= 15;
    issues.push({
      id: 'poor-readability',
      title: 'Lisibilité difficile',
      description: `Index Flesch-Kincaid de ${readabilityScore} indique un texte complexe`,
      severity: 'warning',
      impact: 'medium',
      category: 'ux'
    });
  }

  // Taille de page
  if (checks.large_page_size) {
    score -= 10;
    issues.push({
      id: 'large-page-size',
      title: 'Taille de page importante',
      description: 'La page est volumineuse et peut être lente à charger',
      severity: 'warning',
      impact: 'medium',
      category: 'ux'
    });
  }

  // Contenu
  if (checks.low_content_rate) {
    score -= 12;
    issues.push({
      id: 'low-content-rate',
      title: 'Faible ratio de contenu',
      description: 'Peu de contenu textuel par rapport au code',
      severity: 'warning',
      impact: 'medium',
      category: 'ux'
    });
  }

  return {
    score: Math.max(0, score),
    grade: getGrade(Math.max(0, score)),
    status: getStatus(Math.max(0, score)),
    issues,
    recommendations
  };
};

// Calcul du score technique
export const calculateTechnicalScore = (checks: PageChecks | null | undefined): CategoryScore => {
  // Vérification de sécurité pour les données manquantes
  if (!checks) {
    return getDefaultCategoryScore('technical');
  }

  let score = 100;
  const issues: Issue[] = [];
  const recommendations: Recommendation[] = [];

  // HTTPS
  if (!checks.is_https) {
    score -= 20;
    issues.push({
      id: 'no-https',
      title: 'Site non sécurisé (HTTP)',
      description: 'Le site n\'utilise pas HTTPS',
      severity: 'critical',
      impact: 'high',
      category: 'technical'
    });
  }

  // Doctype
  if (checks.no_doctype) {
    score -= 10;
    issues.push({
      id: 'no-doctype',
      title: 'Doctype manquant',
      description: 'Aucune déclaration DOCTYPE trouvée',
      severity: 'warning',
      impact: 'low',
      category: 'technical'
    });
  }

  // Encoding
  if (checks.no_encoding_meta_tag) {
    score -= 8;
    issues.push({
      id: 'no-encoding',
      title: 'Encodage non spécifié',
      description: 'Meta charset manquant',
      severity: 'warning',
      impact: 'low',
      category: 'technical'
    });
  }

  // Tags dépréciés
  if (checks.deprecated_html_tags) {
    score -= 5;
    issues.push({
      id: 'deprecated-tags',
      title: 'Tags HTML dépréciés',
      description: 'Utilisation de balises HTML obsolètes',
      severity: 'notice',
      impact: 'low',
      category: 'technical'
    });
  }

  return {
    score: Math.max(0, score),
    grade: getGrade(Math.max(0, score)),
    status: getStatus(Math.max(0, score)),
    issues,
    recommendations
  };
};

// Fonction principale pour calculer tous les scores
export const calculateAdvancedScoring = (analysis: InstantPageAnalysisResult | null | undefined): AdvancedScoring => {
  // Vérification de sécurité pour l'analyse manquante
  if (!analysis) {
    const defaultScore = getDefaultCategoryScore('global');
    return {
      global: {
        score: 0,
        grade: 'F',
        status: 'critical',
        trend: 'stable'
      },
      categories: {
        performance: getDefaultCategoryScore('performance'),
        seo: getDefaultCategoryScore('seo'),
        ux: getDefaultCategoryScore('ux'),
        technical: getDefaultCategoryScore('technical')
      },
      totalIssues: {
        critical: 4,
        warnings: 0,
        notices: 0
      },
      priorities: []
    };
  }

  const performance = calculatePerformanceScore(analysis.page_timing, analysis.checks);
  const seo = calculateSeoScore(analysis.meta, analysis.checks);
  const ux = calculateUxScore(analysis.meta, analysis.checks);
  const technical = calculateTechnicalScore(analysis.checks);

  // Score global pondéré
  const globalScore = Math.round(
    (performance.score * 0.3) +
    (seo.score * 0.35) +
    (ux.score * 0.2) +
    (technical.score * 0.15)
  );

  // Comptage des problèmes
  const allIssues = [
    ...performance.issues,
    ...seo.issues,
    ...ux.issues,
    ...technical.issues
  ];

  const totalIssues = {
    critical: allIssues.filter(issue => issue.severity === 'critical').length,
    warnings: allIssues.filter(issue => issue.severity === 'warning').length,
    notices: allIssues.filter(issue => issue.severity === 'notice').length
  };

  // Recommandations prioritaires
  const allRecommendations = [
    ...performance.recommendations,
    ...seo.recommendations,
    ...ux.recommendations,
    ...technical.recommendations
  ];

  const priorities = allRecommendations
    .filter(rec => rec.priority === 'high')
    .slice(0, 5); // Top 5 priorités

  return {
    global: {
      score: globalScore,
      grade: getGrade(globalScore),
      status: getStatus(globalScore),
      trend: 'stable' // À implémenter avec historique
    },
    categories: {
      performance,
      seo,
      ux,
      technical
    },
    totalIssues,
    priorities
  };
};
